// LetterBuilder.jsx — extracted from the original FinanceLetters.jsx and
// parameterized via createLetterBuilder({ apiBase }) so the exact same
// builder/approval UI works for both Admin (/api/admin/letters — auto-
// approved on creation, no submit-for-approval step needed since Admin is
// the approver) and Finance (/api/finance/letters — unchanged draft ->
// submit -> admin-approval lifecycle). Which behavior applies is entirely
// determined by the backend response's `status` field — the existing
// status-based button visibility below just naturally adapts, no
// role-conditional UI logic needed.
import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon, PrinterIcon, ArrowDownTrayIcon, ArrowUpTrayIcon,
  PencilSquareIcon, TrashIcon, CheckCircleIcon,
} from '@heroicons/react/24/outline';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .lt * { box-sizing:border-box; }
  .lt { font-family:'Sora',sans-serif; color:#1a1d2e; animation:ltIn .35s ease both; }
  @keyframes ltIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
  .lt-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .lt-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .lt-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-teal  { background:#0d9488; color:#fff; } .btn-teal:hover  { background:#0b7b71; box-shadow:0 4px 14px rgba(13,148,136,.35); }
  .btn-ghost { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; } .btn-red:hover { background:#ef4444; color:white; }
  .btn-blue  { background:#3b6ef8; color:white; } .btn-blue:hover { background:#2d5ef0; }
  .btn-purple{ background:#8b5cf6; color:white; } .btn-purple:hover { background:#7c3aed; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .type-cards { display:grid; grid-template-columns:repeat(4,1fr); gap:1rem; margin-bottom:1.5rem; }
  .type-card { background:white; border-radius:14px; border:2px solid #e8ebf4; padding:1.2rem; cursor:pointer; transition:all .18s; text-align:center; }
  .type-card:hover { border-color:#0d9488; transform:translateY(-2px); box-shadow:0 6px 20px rgba(13,148,136,.12); }
  .type-card.sel { border-color:#0d9488; background:#f0fdfa; }
  .type-card-icon { font-size:2rem; margin-bottom:8px; }
  .type-card-name { font-size:.88rem; font-weight:700; color:#0f1123; }
  .type-card-desc { font-size:.72rem; color:#9094a8; margin-top:4px; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-hdr { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .tbl { width:100%; border-collapse:collapse; }
  .tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .tbl th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .tbl tbody tr { border-bottom:1px solid #f3f5fb; }
  .tbl tbody tr:hover { background:#fafbff; }
  .tbl td { padding:11px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .pill { display:inline-flex; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; }
  .pill-draft    { background:#f0f2f7; color:#6b7194; }
  .pill-pending  { background:#fffbeb; color:#f59e0b; }
  .pill-approved { background:#ecfdf5; color:#10c98e; }
  .pill-rejected { background:#fef2f2; color:#ef4444; }
  .lt-cards { display:none; padding:12px; }
  .lt-card  { background:white; border:1px solid #e8ebf4; border-radius:14px; padding:13px; margin-bottom:8px; }
  .ltc-top  { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .ltc-row  { display:flex; justify-content:space-between; align-items:center; padding:4px 0; gap:10px; border-bottom:1px solid #f5f6fb; font-size:.78rem; }
  .ltc-row:last-of-type { border-bottom:none; }
  .ltc-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; white-space:nowrap; flex-shrink:0; }
  .ltc-actions { display:flex; gap:6px; flex-wrap:wrap; margin-top:10px; padding-top:8px; border-top:1px solid #f0f2f8; }
  .empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spin { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#0d9488; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  .builder { display:grid; grid-template-columns:380px 1fr; gap:1.2rem; align-items:start; }
  .form-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:16px; }
  .sec-label { font-size:.65rem; font-weight:700; color:#c0c5dc; text-transform:uppercase; letter-spacing:.09em; margin:14px 0 6px; border-top:1px solid #f0f2f8; padding-top:10px; }
  .sec-label:first-child { border-top:none; padding-top:0; margin-top:0; }
  .fg  { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
  .form-grid > *, .fg > * { min-width:0; }
  .fg1 { display:grid; grid-template-columns:1fr; gap:8px; }
  .ff  { display:flex; flex-direction:column; gap:4px; }
  .fl  { font-size:.68rem; font-weight:700; color:#6b7194; text-transform:uppercase; letter-spacing:.06em; }
  .fi,.fs,.fta { font-family:'Sora',sans-serif; font-size:.78rem; padding:7px 10px; border:1.5px solid #e0e4f0; border-radius:8px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%;min-width:0; }
  .fi:focus,.fs:focus,.fta:focus { border-color:#0d9488; background:white; }
  .fta { resize:vertical; min-height:60px; line-height:1.5; }
  .align-btns { display:flex; gap:4px; }
  .align-btn { padding:4px 10px; border-radius:6px; border:1.5px solid #e0e4f0; background:white; font-size:.72rem; font-weight:600; cursor:pointer; color:#9094a8; font-family:'Sora',sans-serif; transition:.12s; }
  .align-btn.on { background:#0d9488; color:white; border-color:#0d9488; }
  .sig-toggle { display:flex; gap:4px; }
  .sig-btn { flex:1; padding:7px 8px; border-radius:8px; border:1.5px solid #e0e4f0; background:white; font-size:.74rem; font-weight:600; cursor:pointer; color:#9094a8; font-family:'Sora',sans-serif; transition:.12s; text-align:center; }
  .sig-btn.on { background:#0d9488; color:white; border-color:#0d9488; }
  .sig-upload { border:1.5px dashed #e0e4f0; border-radius:9px; padding:10px; text-align:center; cursor:pointer; transition:.15s; position:relative; }
  .sig-upload:hover { border-color:#0d9488; background:#f0fdfa; }
  .sig-upload input { position:absolute; inset:0; opacity:0; cursor:pointer; width:100%; height:100%; }
  .sig-preview { max-height:44px; max-width:160px; margin:0 auto 4px; display:block; border-radius:4px; }
  .sig-upload-label { font-size:.72rem; color:#9094a8; }
  .img-upload { border:1.5px dashed #e0e4f0; border-radius:9px; padding:8px 10px; cursor:pointer; transition:.15s; position:relative; display:flex; align-items:center; gap:8px; }
  .img-upload:hover { border-color:#0d9488; }
  .img-upload input { position:absolute; inset:0; opacity:0; cursor:pointer; width:100%; height:100%; }
  .img-upload-label { font-size:.74rem; color:#9094a8; }
  .preview-wrap { position:sticky; top:20px; }
  .preview-label { font-size:.72rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; margin-bottom:8px; }
  .letter-paper { background:white; border:1px solid #e0e4f0; border-radius:12px; padding:36px 40px; min-height:500px; font-family:'Times New Roman',serif; font-size:13px; line-height:1.7; color:#111; box-shadow:0 4px 20px rgba(0,0,0,.06); }
  .letter-paper .lp-logo { margin-bottom:20px; }
  .letter-paper .lp-logo img { max-height:70px; max-width:220px; }
  .letter-paper .lp-to   { margin-bottom:10px; white-space:pre-line; }
  .letter-paper .lp-from { margin-bottom:16px; white-space:pre-line; }
  .letter-paper .lp-body { margin-bottom:24px; white-space:pre-line; }
  .letter-paper .lp-close{ margin-bottom:28px; white-space:pre-line; }
  .letter-paper .lp-sigs { display:flex; gap:40px; }
  .letter-paper .lp-sig-block { flex:1; }
  .letter-paper .lp-sig-block img { max-height:50px; margin-bottom:4px; display:block; }
  @media (max-width:900px) {
    .builder { grid-template-columns:1fr; }
    .type-cards { grid-template-columns:1fr 1fr; }
  }
  @media (max-width:600px) {
    .type-cards { grid-template-columns:1fr; }
  }
  @media (max-width:768px) {
    .lt-tbl-wrap { display:none !important; }
    .lt-cards { display:block; }
  }
`;

const LETTER_TYPES = [
  { key: 'character_certificate', label: 'Character Certificate', icon: '🏅', desc: 'Certifies student character and conduct',        target: 'student' },
  { key: 'school_leaving',        label: 'School Leaving',        icon: '🎓', desc: 'Official school leaving / transfer certificate', target: 'student' },
  { key: 'experience_letter',     label: 'Experience Letter',     icon: '💼', desc: 'Work experience letter for staff who have exited', target: 'teacher' },
  { key: 'appointment_letter',    label: 'Appointment Letter',    icon: '📋', desc: 'Official appointment / offer letter for staff',  target: 'teacher' },
];
const STAFF_TARGET_TYPES = [
  { key: 'teacher',          label: 'Teacher' },
  { key: 'librarian',        label: 'Librarian' },
  { key: 'hostel_member',    label: 'Hostel Member' },
  { key: 'finance',          label: 'Finance Member' },
  { key: 'coordinator',      label: 'Coordinator' },
  { key: 'non_portal_staff', label: 'Non-Portal Staff' },
];

const ALIGNS      = ['left','center','right'];
const ALIGNS_BODY = ['left','center','right','justify'];
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' }) : '—';
const today   = () => new Date().toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' });

const personNameOf = (pre) => pre.teacherName || pre.librarianName || pre.hostelMemberName
  || pre.financeMemberName || pre.coordinatorName || pre.nonPortalStaffName || '[Name]';
const roleTitleOf = (pre, subj) => {
  if (pre.librarianName) return 'Librarian';
  if (pre.hostelMemberName) return pre.designation || 'Hostel Staff';
  if (pre.financeMemberName) return 'Finance Member';
  if (pre.coordinatorName) return 'Coordinator';
  if (pre.nonPortalStaffName) return pre.designation || 'Staff';
  return `${subj} Teacher`;
};

const defaultBody = (type, pre = {}, school = {}) => {
  const todayStr = today();
  const sName = school.name || '[School Name]';

  if (type === 'character_certificate') {
    return `This is to certify that ${pre.studentName || '[Student Name]'} has been a student of ${sName} from [Start Date] to [End Date].

During their time at our institution, ${pre.studentName || 'the student'} has demonstrated exemplary character, discipline, and dedication to academic excellence. They have been found to be of good moral character and conduct.

We wish them all the best in their future endeavors.

Issued on: ${todayStr}`;
  }

  if (type === 'school_leaving') {
    return `This is to certify that ${pre.studentName || '[Student Name]'}, Admission No. ${pre.admissionNo || '[ADM-NO]'}, Class ${pre.class || '[Class]'} – Section ${pre.section || '[Section]'} has been a bonafide student of ${sName}.

This certificate is being issued upon request for admission/transfer purposes.

Date of Leaving: [Date]
Reason: Transfer / Admission Elsewhere

We wish the student a bright academic future.

Issued on: ${todayStr}`;
  }

  if (type === 'experience_letter') {
    const from = pre.joiningDate ? fmtDate(pre.joiningDate) : '[Start Date]';
    const to   = pre.exitDate    ? fmtDate(pre.exitDate)    : (pre.resignDate ? fmtDate(pre.resignDate) : 'Present');
    const subj = pre.subjects?.join(', ') || '[Subject]';
    const personName = personNameOf(pre);
    const roleTitle = roleTitleOf(pre, subj);
    return `This is to certify that ${personName}, Employee ID: ${pre.employeeId || '[EMP-ID]'} has served as a ${roleTitle} at ${sName}.

During their tenure from ${from} to ${to}, they have demonstrated professionalism, dedication and excellent teaching skills.

They are leaving on their own accord and we wish them well in their future career.

This letter is issued upon request for future employment purposes.

Issued on: ${todayStr}`;
  }

  if (type === 'appointment_letter') {
    const joining     = pre.joiningDate ? fmtDate(pre.joiningDate) : '[Joining Date]';
    const designation = pre.designation || roleTitleOf(pre, pre.subjects?.join(', ') || '');
    const salary      = pre.monthlySalary ? `Rs. ${Number(pre.monthlySalary).toLocaleString()}` : '[Salary]';
    const subj        = pre.subjects?.join(', ') || '[Subject]';
    const personName  = personNameOf(pre);
    return `Dear ${personName},

We are pleased to offer you the position of ${designation}${subj ? ` (${subj})` : ''} at ${sName}, effective from ${joining}.

Your appointment is subject to the following terms and conditions:

1. Designation:    ${designation}
2. Joining Date:   ${joining}
3. Monthly Salary: ${salary}
4. Working Hours:  As per school schedule
5. Probation:      3 months from date of joining

You are expected to perform your duties with dedication, maintain confidentiality of school matters, and adhere to all institutional policies.

Please sign and return the duplicate copy of this letter as confirmation of your acceptance.

We look forward to your valuable contribution to our institution.

Issued on: ${todayStr}`;
  }

  return '';
};

function ImageUploader({ label, value, onChange, maxKB = 500, preview = 'logo' }) {
  const inputRef = useRef();
  return (
    <div className="ff">
      <label className="fl">{label}</label>
      <div className="img-upload" onClick={() => inputRef.current?.click()}>
        <input ref={inputRef} type="file" accept="image/*"
          onChange={e => {
            const file = e.target.files?.[0];
            if (!file) return;
            if (file.size > maxKB * 1024) { toast.error(`Image must be under ${maxKB}KB`); return; }
            const reader = new FileReader();
            reader.onload = ev => onChange(ev.target.result);
            reader.readAsDataURL(file);
            e.target.value = '';
          }}
        />
        {value
          ? <img src={value} alt="" style={{maxHeight: preview==='sig' ? 36 : 44, maxWidth:160, borderRadius:4, flexShrink:0}}/>
          : <span style={{fontSize:'1.2rem'}}>📁</span>
        }
        <span className="img-upload-label">{value ? 'Click to change' : `Upload ${label}`}</span>
        {value && <button type="button" style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer',color:'#ef4444',fontSize:'.8rem'}} onClick={e=>{e.stopPropagation();onChange('');}}>✕ Remove</button>}
      </div>
    </div>
  );
}

function LetterPreview({ f }) {
  const renderSig = (imgUrl, name, title) => (
    <div className="lp-sig-block">
      {imgUrl
        ? <img src={imgUrl} alt="signature"/>
        : <div style={{height:44,borderBottom:'1px solid #999',marginBottom:4,width:120}}/>
      }
      <strong style={{display:'block',fontSize:13}}>{name || '_______________'}</strong>
      <span style={{fontSize:12,color:'#6b7194'}}>{title || ''}</span>
    </div>
  );

  return (
    <div className="letter-paper">
      {f.logoUrl && (
        <div className="lp-logo" style={{textAlign:f.logoAlign}}>
          <img src={f.logoUrl} alt="logo"/>
        </div>
      )}
      {f.toName   && <div className="lp-to"   style={{textAlign:f.toAlign}}>{f.toName}</div>}
      {f.fromText && <div className="lp-from" style={{textAlign:f.fromAlign}}>{f.fromText}</div>}
      <div className="lp-body" style={{textAlign:f.bodyAlign}}>
        {f.body || <span style={{color:'#c0c5dc',fontStyle:'italic'}}>Letter body will appear here…</span>}
      </div>
      <div className="lp-close" style={{textAlign:f.closingAlign}}>{f.closing}</div>
      <div className="lp-sigs" style={{justifyContent: f.signatureType==='both' ? 'space-between' : f.closingAlign==='center' ? 'center' : f.closingAlign==='right' ? 'flex-end' : 'flex-start'}}>
        {(f.signatureType === 'principal' || f.signatureType === 'both') &&
          renderSig(f.principalSigImg, f.principalName, f.principalTitle)}
        {(f.signatureType === 'finance'   || f.signatureType === 'both') &&
          renderSig(f.financeSigImg, f.financeName, f.financeTitle)}
      </div>
    </div>
  );
}

function buildPrintHTML(f, typeLabel) {
  const sigBlock = (img, name, title) => `
    <div style="flex:1;min-width:140px;">
      ${img ? `<img src="${img}" style="max-height:50px;margin-bottom:4px;display:block;"/>` : `<div style="height:44px;border-bottom:1px solid #999;width:130px;margin-bottom:4px;"></div>`}
      <strong style="display:block;font-size:13px;">${name || '_______________'}</strong>
      <span style="font-size:12px;color:#555;">${title || ''}</span>
    </div>`;

  const showPrincipal = f.signatureType === 'principal' || f.signatureType === 'both';
  const showFinance   = f.signatureType === 'finance'   || f.signatureType === 'both';
  const sigJustify    = f.signatureType === 'both' ? 'space-between' : f.closingAlign === 'center' ? 'center' : f.closingAlign === 'right' ? 'flex-end' : 'flex-start';

  return `<!DOCTYPE html><html><head><title>${typeLabel}</title>
  <style>
    body { font-family:'Times New Roman',serif; font-size:14px; color:#111; padding:50px 60px; line-height:1.7; margin:0; }
    @page { margin:2cm; }
  </style></head><body>
    ${f.logoUrl ? `<div style="text-align:${f.logoAlign};margin-bottom:20px;"><img src="${f.logoUrl}" style="max-height:70px;max-width:220px;" /></div>` : ''}
    ${f.toName   ? `<p style="text-align:${f.toAlign};margin-bottom:10px;white-space:pre-line;">${f.toName}</p>` : ''}
    ${f.fromText ? `<p style="text-align:${f.fromAlign};margin-bottom:16px;white-space:pre-line;">${f.fromText}</p>` : ''}
    <p style="text-align:${f.bodyAlign};margin-bottom:24px;white-space:pre-line;">${f.body || ''}</p>
    <p style="text-align:${f.closingAlign};margin-bottom:28px;white-space:pre-line;">${f.closing || ''}</p>
    <div style="display:flex;gap:40px;justify-content:${sigJustify};">
      ${showPrincipal ? sigBlock(f.principalSigImg, f.principalName, f.principalTitle) : ''}
      ${showFinance   ? sigBlock(f.financeSigImg,   f.financeName,   f.financeTitle)   : ''}
    </div>
    <script>window.onload = () => { window.print(); };<\/script>
  </body></html>`;
}

export function createLetterBuilder({ apiBase }) {
  return function LetterBuilder() {
    const [letters,       setLetters]       = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [step,          setStep]          = useState('list');
    const [selectedType,  setSelectedType]  = useState(null);
    const [builderLetter, setBuilderLetter] = useState(null);
    const [students,      setStudents]      = useState([]);
    const [teachers,      setTeachers]      = useState([]);
    const [librarians,    setLibrarians]    = useState([]);
    const [hostelMembers, setHostelMembers] = useState([]);
    const [financeMembers,setFinanceMembers]= useState([]);
    const [coordinators,  setCoordinators]  = useState([]);
    const [nonPortalStaff,setNonPortalStaff]= useState([]);
    const [school,        setSchool]        = useState({});
    const [saving,        setSaving]        = useState(false);

    const EMPTY_FORM = {
      targetType: 'student', targetId: '',
      logoUrl: '', logoAlign: 'center',
      toName: 'To Whom It May Concern,', toAlign: 'left',
      fromText: `Date: ${today()}`, fromAlign: 'left',
      body: '', bodyAlign: 'justify',
      closing: 'Yours Sincerely,', closingAlign: 'left',
      signatureType: 'principal',
      principalSigImg: '', principalName: '', principalTitle: 'Principal',
      financeSigImg:   '', financeName:   '', financeTitle:   'Finance Manager',
    };

    const [form, setForm] = useState(EMPTY_FORM);
    const sf = (f, v) => setForm(p => ({ ...p, [f]: v }));

    const typeConfig = LETTER_TYPES.find(t => t.key === selectedType);

    useEffect(() => {
      loadLetters();
      api.get(`${apiBase}/letter-students`).then(r => setStudents(r.data || [])).catch(() => {});
      api.get(`${apiBase}/letter-teachers`).then(r => setTeachers(r.data || [])).catch(() => {});
      api.get(`${apiBase}/letter-librarians`).then(r => setLibrarians(r.data || [])).catch(() => {});
      api.get(`${apiBase}/letter-hostel-members`).then(r => setHostelMembers(r.data || [])).catch(() => {});
      api.get(`${apiBase}/letter-finance-members`).then(r => setFinanceMembers(r.data || [])).catch(() => {});
      api.get(`${apiBase}/letter-coordinators`).then(r => setCoordinators(r.data || [])).catch(() => {});
      api.get(`${apiBase}/letter-non-portal-staff`).then(r => setNonPortalStaff(r.data || [])).catch(() => {});
      api.get('/settings/school').then(r => setSchool(r?.data || {})).catch(() => {});
    }, []);

    const loadLetters = () => {
      setLoading(true);
      api.get(`${apiBase}/letters`)
        .then(r => setLetters(r.data || []))
        .catch(() => toast.error('Failed to load letters'))
        .finally(() => setLoading(false));
    };

    const chooseType = (type) => {
      setSelectedType(type);
      const tc = LETTER_TYPES.find(t => t.key === type);
      setForm({ ...EMPTY_FORM, targetType: tc.target, targetId: '', body: '' });
      setBuilderLetter(null);
      setStep('builder');
    };

    const editLetter = (letter) => {
      setSelectedType(letter.type);
      setBuilderLetter(letter);
      setForm({
        targetType:     letter.targetType     || 'student',
        targetId:       letter.targetId       || '',
        logoUrl:        letter.logoUrl        || '',
        logoAlign:      letter.logoAlign      || 'center',
        toName:         letter.toName         || 'To Whom It May Concern,',
        toAlign:        letter.toAlign        || 'left',
        fromText:       letter.fromText       || `Date: ${today()}`,
        fromAlign:      letter.fromAlign      || 'left',
        body:           letter.body           || '',
        bodyAlign:      letter.bodyAlign      || 'justify',
        closing:        letter.closing        || 'Yours Sincerely,',
        closingAlign:   letter.closingAlign   || 'left',
        signatureType:  letter.signatureType  || 'principal',
        principalSigImg:letter.principalSigImg|| '',
        principalName:  letter.principalName  || '',
        principalTitle: letter.principalTitle || 'Principal',
        financeSigImg:  letter.financeSigImg  || '',
        financeName:    letter.financeName    || '',
        financeTitle:   letter.financeTitle   || 'Finance Manager',
      });
      setStep('builder');
    };

    const staffPoolFor = (targetType) => ({
      librarian: librarians, hostel_member: hostelMembers,
      finance: financeMembers, coordinator: coordinators, non_portal_staff: nonPortalStaff,
    }[targetType] || teachers);

    const handleTargetChange = (id) => {
      sf('targetId', id);
      if (!id) return;
      let pre = {};
      if (form.targetType === 'student') {
        const s = students.find(s => String(s._id) === String(id));
        if (s) pre = { studentName: s.user?.name, admissionNo: s.admissionNo, class: s.class?.name, section: s.section };
      } else if (form.targetType === 'teacher') {
        const t = teachers.find(t => String(t._id) === String(id));
        if (t) pre = {
          teacherName:   t.user?.name,
          employeeId:    t.employeeId,
          subjects:      t.subjects,
          joiningDate:   t.joiningDate,
          designation:   t.designation || 'Teacher',
          monthlySalary: t.monthlySalary || 0,
          exitType:      t.exitType,
          exitDate:      t.exitDate,
          resignDate:    t.exitType === 'resigned'   ? t.exitDate : null,
          terminateDate: t.exitType === 'terminated' ? t.exitDate : null,
        };
      } else {
        const pool = staffPoolFor(form.targetType);
        const p = pool.find(p => String(p._id) === String(id));
        if (p) {
          const nameKey = {
            librarian: 'librarianName', hostel_member: 'hostelMemberName',
            finance: 'financeMemberName', coordinator: 'coordinatorName', non_portal_staff: 'nonPortalStaffName',
          }[form.targetType];
          pre = {
            [nameKey]: p.user?.name, employeeId: p.employeeId, joiningDate: p.joiningDate,
            designation: p.designation || (form.targetType === 'librarian' ? 'Librarian' : form.targetType === 'coordinator' ? 'Coordinator' : form.targetType === 'finance' ? 'Finance Member' : 'Staff'),
            exitType: p.exitType, exitDate: p.exitDate,
            resignDate:    p.exitType === 'resigned'   ? p.exitDate : null,
            terminateDate: p.exitType === 'terminated' ? p.exitDate : null,
          };
        }
      }
      setForm(p => ({ ...p, body: defaultBody(selectedType, pre, school) }));
    };

    const saveLetter = async () => {
      if (!form.body.trim()) return toast.error('Letter body is required');
      setSaving(true);
      try {
        const payload = { type: selectedType, ...form };
        if (builderLetter) {
          await api.put(`${apiBase}/letters/${builderLetter._id}`, payload);
          toast.success('Letter updated');
        } else {
          const { data } = await api.post(`${apiBase}/letters`, payload);
          toast.success(data.status === 'approved' ? 'Letter created' : 'Letter saved as draft');
        }
        loadLetters();
        setStep('list');
      } catch { toast.error('Failed to save'); }
      finally { setSaving(false); }
    };

    const submitForApproval = async (letter) => {
      try {
        await api.post(`${apiBase}/letters/${letter._id}/submit`, { note: 'Requesting approval' });
        toast.success('Submitted for admin approval');
        loadLetters();
      } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    const deleteLetter = async (id) => {
      if (!window.confirm('Delete this letter?')) return;
      try { await api.delete(`${apiBase}/letters/${id}`); loadLetters(); toast.success('Deleted'); }
      catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    const printLetter = (letter) => {
      const typeLabel = LETTER_TYPES.find(t => t.key === letter.type)?.label || letter.type;
      const f = {
        logoUrl:        letter.logoUrl        || '',
        logoAlign:      letter.logoAlign      || 'center',
        toName:         letter.toName         || '',
        toAlign:        letter.toAlign        || 'left',
        fromText:       letter.fromText       || '',
        fromAlign:      letter.fromAlign      || 'left',
        body:           letter.body           || '',
        bodyAlign:      letter.bodyAlign      || 'justify',
        closing:        letter.closing        || '',
        closingAlign:   letter.closingAlign   || 'left',
        signatureType:  letter.signatureType  || 'principal',
        principalSigImg:letter.principalSigImg|| '',
        principalName:  letter.principalName  || '',
        principalTitle: letter.principalTitle || 'Principal',
        financeSigImg:  letter.financeSigImg  || '',
        financeName:    letter.financeName    || '',
        financeTitle:   letter.financeTitle   || 'Finance Manager',
      };
      const win = window.open('', '_blank', 'width=820,height=1000');
      win.document.write(buildPrintHTML(f, typeLabel));
      win.document.close();
    };

    const downloadLetter = (letter) => {
      const typeLabel = LETTER_TYPES.find(t => t.key === letter.type)?.label || letter.type;
      const f = {
        logoUrl: letter.logoUrl||'', logoAlign: letter.logoAlign||'center',
        toName: letter.toName||'', toAlign: letter.toAlign||'left',
        fromText: letter.fromText||'', fromAlign: letter.fromAlign||'left',
        body: letter.body||'', bodyAlign: letter.bodyAlign||'justify',
        closing: letter.closing||'', closingAlign: letter.closingAlign||'left',
        signatureType: letter.signatureType||'principal',
        principalSigImg: letter.principalSigImg||'', principalName: letter.principalName||'', principalTitle: letter.principalTitle||'Principal',
        financeSigImg: letter.financeSigImg||'', financeName: letter.financeName||'', financeTitle: letter.financeTitle||'Finance Manager',
      };
      const html = buildPrintHTML(f, typeLabel).replace(
        '<script>window.onload = () => { window.print(); };<\/script>',
        '<script>window.onload = () => { window.print(); window.onafterprint = () => window.close(); };<\/script>'
      );
      const win = window.open('', '_blank', 'width=820,height=1000');
      win.document.write(html);
      win.document.close();
      toast('Use "Save as PDF" in the print dialog to download', { icon: '💡', duration: 4000 });
    };

    if (step === 'builder') {
      const staffPool = staffPoolFor(form.targetType);
      const people = form.targetType === 'student'
        ? (['character_certificate','school_leaving'].includes(selectedType) ? students.filter(s => s.isActive === false) : students)
        : (selectedType === 'experience_letter' ? staffPool.filter(t => !!t.exitType)
          : selectedType === 'appointment_letter' ? staffPool.filter(t => !t.exitType)
          : staffPool);
      const appointedStaffIds = new Set(
        letters.filter(l => l.type === 'appointment_letter' && l.targetId && l.targetType === form.targetType).map(l => String(l.targetId))
      );
      const isStaffLetter = ['experience_letter','appointment_letter'].includes(selectedType);

      return (
        <div className="lt"><style>{css}</style>
          <div className="lt-hdr">
            <div>
              <div className="lt-title">{typeConfig?.label}</div>
              <div className="lt-sub">Design the letter layout, content and signatures</div>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-ghost" onClick={() => setStep('list')}>← Back</button>
              <button className="btn btn-teal"  onClick={saveLetter} disabled={saving}>
                <CheckCircleIcon/>{saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          <div className="builder">
            <div className="form-panel">

              <div className="sec-label">Recipient</div>
              {isStaffLetter && (
                <div className="ff">
                  <label className="fl">Staff Type</label>
                  <select className="fs" value={form.targetType} onChange={e => { sf('targetType', e.target.value); sf('targetId', ''); }}>
                    {STAFF_TARGET_TYPES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
              )}
              <div className="ff">
                <label className="fl">Select {form.targetType === 'student' ? 'Student' : STAFF_TARGET_TYPES.find(s=>s.key===form.targetType)?.label || 'Person'}</label>
                <select className="fs" value={form.targetId} onChange={e => handleTargetChange(e.target.value)}>
                  <option value="">— Select to auto-fill body —</option>
                  {people.map(p => (
                    <option key={p._id} value={p._id}>
                      {p.user?.name}
                      {form.targetType === 'student' ? ` (${p.admissionNo})` : ` — ${p.employeeId||''}`}
                      {isStaffLetter && p.exitDate ? ` [${p.exitType||'exited'}]` : ''}
                      {selectedType === 'appointment_letter' && appointedStaffIds.has(String(p._id)) ? ' — ✓ Appointed' : ''}
                    </option>
                  ))}
                </select>
                {people.length === 0 && (
                  <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:4}}>
                    {selectedType === 'experience_letter' && `No exited ${STAFF_TARGET_TYPES.find(s=>s.key===form.targetType)?.label.toLowerCase()}s found — only staff marked as exited are eligible.`}
                    {['character_certificate','school_leaving'].includes(selectedType) && 'No left students found — only students in the Student Left list are eligible.'}
                  </div>
                )}
              </div>

              <div className="sec-label">Header Logo</div>
              <ImageUploader label="School Logo" value={form.logoUrl} onChange={v => sf('logoUrl', v)} maxKB={500} preview="logo"/>
              <div className="ff" style={{marginTop:6}}>
                <label className="fl">Logo Alignment</label>
                <div className="align-btns">{ALIGNS.map(a => <button key={a} className={`align-btn${form.logoAlign===a?' on':''}`} onClick={() => sf('logoAlign',a)}>{a}</button>)}</div>
              </div>

              <div className="sec-label">To (Addressee)</div>
              <div className="ff">
                <label className="fl">Address Line</label>
                <input className="fi" value={form.toName} onChange={e => sf('toName',e.target.value)}/>
              </div>
              <div className="ff" style={{marginTop:6}}>
                <label className="fl">Alignment</label>
                <div className="align-btns">{ALIGNS.map(a => <button key={a} className={`align-btn${form.toAlign===a?' on':''}`} onClick={() => sf('toAlign',a)}>{a}</button>)}</div>
              </div>

              <div className="sec-label">Date / Reference</div>
              <div className="ff">
                <label className="fl">Date / From Text</label>
                <textarea className="fta" rows={2} value={form.fromText} onChange={e => sf('fromText',e.target.value)} placeholder={`Date: ${today()}`}/>
              </div>
              <div className="ff" style={{marginTop:6}}>
                <label className="fl">Alignment</label>
                <div className="align-btns">{ALIGNS.map(a => <button key={a} className={`align-btn${form.fromAlign===a?' on':''}`} onClick={() => sf('fromAlign',a)}>{a}</button>)}</div>
              </div>

              <div className="sec-label">Letter Body</div>
              <div className="ff">
                <label className="fl">Body (fully editable)</label>
                <textarea className="fta" rows={10} style={{minHeight:160}} value={form.body} onChange={e => sf('body',e.target.value)}/>
              </div>
              <div className="ff" style={{marginTop:6}}>
                <label className="fl">Alignment</label>
                <div className="align-btns">{ALIGNS_BODY.map(a => <button key={a} className={`align-btn${form.bodyAlign===a?' on':''}`} onClick={() => sf('bodyAlign',a)}>{a}</button>)}</div>
              </div>

              <div className="sec-label">Closing Line</div>
              <div className="ff">
                <label className="fl">Closing Text</label>
                <input className="fi" value={form.closing} onChange={e => sf('closing',e.target.value)}/>
              </div>
              <div className="ff" style={{marginTop:6}}>
                <label className="fl">Alignment</label>
                <div className="align-btns">{ALIGNS.map(a => <button key={a} className={`align-btn${form.closingAlign===a?' on':''}`} onClick={() => sf('closingAlign',a)}>{a}</button>)}</div>
              </div>

              <div className="sec-label">Signature(s)</div>
              <div className="ff" style={{marginBottom:10}}>
                <label className="fl">Who Signs</label>
                <div className="sig-toggle">
                  {[{k:'principal',l:'Principal'},{k:'finance',l:'Finance'},{k:'both',l:'Both'}].map(s => (
                    <button key={s.k} className={`sig-btn${form.signatureType===s.k?' on':''}`} onClick={() => sf('signatureType',s.k)}>{s.l}</button>
                  ))}
                </div>
              </div>

              {(form.signatureType==='principal'||form.signatureType==='both') && (
                <>
                  <div style={{fontSize:'.7rem',fontWeight:700,color:'#0d9488',margin:'8px 0 4px'}}>PRINCIPAL</div>
                  <div className="fg1">
                    <ImageUploader label="Signature Image" value={form.principalSigImg} onChange={v => sf('principalSigImg',v)} maxKB={200} preview="sig"/>
                    <div className="ff">
                      <label className="fl">Name</label>
                      <input className="fi" value={form.principalName} onChange={e => sf('principalName',e.target.value)} placeholder="e.g. Mr. Ahmad Khan"/>
                    </div>
                    <div className="ff">
                      <label className="fl">Title</label>
                      <input className="fi" value={form.principalTitle} onChange={e => sf('principalTitle',e.target.value)} placeholder="Principal"/>
                    </div>
                  </div>
                </>
              )}

              {(form.signatureType==='finance'||form.signatureType==='both') && (
                <>
                  <div style={{fontSize:'.7rem',fontWeight:700,color:'#0d9488',margin:'8px 0 4px'}}>FINANCE</div>
                  <div className="fg1">
                    <ImageUploader label="Signature Image" value={form.financeSigImg} onChange={v => sf('financeSigImg',v)} maxKB={200} preview="sig"/>
                    <div className="ff">
                      <label className="fl">Name</label>
                      <input className="fi" value={form.financeName} onChange={e => sf('financeName',e.target.value)} placeholder="e.g. Ms. Fatima"/>
                    </div>
                    <div className="ff">
                      <label className="fl">Title</label>
                      <input className="fi" value={form.financeTitle} onChange={e => sf('financeTitle',e.target.value)} placeholder="Finance Manager"/>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="preview-wrap">
              <div className="preview-label">Live Preview</div>
              <LetterPreview f={form}/>
            </div>
          </div>
        </div>
      );
    }

    if (step === 'choose-type') {
      return (
        <div className="lt"><style>{css}</style>
          <div className="lt-hdr">
            <div><div className="lt-title">New Letter</div><div className="lt-sub">Choose the type of letter to create</div></div>
            <button className="btn btn-ghost" onClick={() => setStep('list')}>Cancel</button>
          </div>
          <div className="type-cards">
            {LETTER_TYPES.map(t => (
              <div key={t.key} className={`type-card${selectedType===t.key?' sel':''}`} onClick={() => chooseType(t.key)}>
                <div className="type-card-icon">{t.icon}</div>
                <div className="type-card-name">{t.label}</div>
                <div className="type-card-desc">{t.desc}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="lt"><style>{css}</style>
        <div className="lt-hdr">
          <div>
            <div className="lt-title">Staff Letters</div>
            <div className="lt-sub">Character certificates, leaving certificates, experience & appointment letters</div>
          </div>
          <button className="btn btn-teal" onClick={() => setStep('choose-type')}><PlusIcon/>New Letter</button>
        </div>

        <div className="panel">
          <div className="panel-hdr"><div className="panel-title">All Letters ({letters.length})</div></div>
          {loading
            ? <div className="loading"><div className="spin"/>Loading…</div>
            : !letters.length
            ? (
              <div className="empty">
                No letters yet.<br/>
                <button className="btn btn-teal btn-sm" style={{marginTop:12}} onClick={() => setStep('choose-type')}>
                  <PlusIcon/>Create First Letter
                </button>
              </div>
            ) : (
              <>
              <div className="lt-tbl-wrap" style={{overflowX:'auto'}}>
                <table className="tbl">
                  <thead>
                    <tr><th>Type</th><th>For</th><th>Status</th><th>Note</th><th>Created</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {letters.map(l => (
                      <tr key={l._id}>
                        <td>
                          <div style={{fontWeight:600}}>{LETTER_TYPES.find(t => t.key === l.type)?.label || l.type}</div>
                          <div style={{fontSize:'.68rem',color:'#9094a8'}}>{LETTER_TYPES.find(t => t.key === l.type)?.icon}</div>
                        </td>
                        <td>
                          {l.targetName || '—'}
                          <div style={{fontSize:'.7rem',color:'#9094a8',textTransform:'capitalize'}}>{l.targetType}</div>
                        </td>
                        <td>
                          <span className={`pill pill-${l.status==='pending_approval'?'pending':l.status}`}>
                            {l.status.replace('_',' ')}
                          </span>
                        </td>
                        <td style={{fontSize:'.74rem',color:l.status==='rejected'?'#ef4444':'#10c98e',maxWidth:160}}>
                          {l.adminNote || '—'}
                        </td>
                        <td style={{fontSize:'.74rem',color:'#9094a8',whiteSpace:'nowrap'}}>{fmtDate(l.createdAt)}</td>
                        <td>
                          <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                            {['draft','rejected'].includes(l.status) && (
                              <button className="btn btn-ghost btn-sm" onClick={() => editLetter(l)} title="Edit">
                                <PencilSquareIcon/>
                              </button>
                            )}
                            {['draft','rejected'].includes(l.status) && (
                              <button className="btn btn-teal btn-sm" onClick={() => submitForApproval(l)} title="Submit for Approval">
                                <ArrowUpTrayIcon/>
                              </button>
                            )}
                            {l.status === 'approved' && (
                              <button className="btn btn-blue btn-sm" onClick={() => printLetter(l)} title="Print">
                                <PrinterIcon/>Print
                              </button>
                            )}
                            {l.status === 'approved' && (
                              <button className="btn btn-purple btn-sm" onClick={() => downloadLetter(l)} title="Download PDF">
                                <ArrowDownTrayIcon/>PDF
                              </button>
                            )}
                            {l.status !== 'approved' && (
                              <button className="btn btn-red btn-sm" onClick={() => deleteLetter(l._id)} title="Delete">
                                <TrashIcon/>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="lt-cards">
                {letters.map(l => (
                  <div key={l._id} className="lt-card">
                    <div className="ltc-top">
                      <div>
                        <div style={{fontWeight:700,fontSize:'.86rem',color:'#0f1123'}}>
                          {LETTER_TYPES.find(t => t.key === l.type)?.icon} {LETTER_TYPES.find(t => t.key === l.type)?.label || l.type}
                        </div>
                      </div>
                      <span className={`pill pill-${l.status==='pending_approval'?'pending':l.status}`}>
                        {l.status.replace('_',' ')}
                      </span>
                    </div>
                    <div className="ltc-row">
                      <span className="ltc-label">For</span>
                      <span style={{textAlign:'right'}}>{l.targetName || '—'} <span style={{color:'#9094a8',textTransform:'capitalize'}}>({l.targetType})</span></span>
                    </div>
                    <div className="ltc-row">
                      <span className="ltc-label">Note</span>
                      <span style={{color:l.status==='rejected'?'#ef4444':'#10c98e',textAlign:'right',maxWidth:180}}>{l.adminNote || '—'}</span>
                    </div>
                    <div className="ltc-row">
                      <span className="ltc-label">Created</span>
                      <span style={{color:'#9094a8'}}>{fmtDate(l.createdAt)}</span>
                    </div>
                    <div className="ltc-actions">
                      {['draft','rejected'].includes(l.status) && (
                        <button className="btn btn-ghost btn-sm" onClick={() => editLetter(l)} title="Edit">
                          <PencilSquareIcon/>Edit
                        </button>
                      )}
                      {['draft','rejected'].includes(l.status) && (
                        <button className="btn btn-teal btn-sm" onClick={() => submitForApproval(l)} title="Submit for Approval">
                          <ArrowUpTrayIcon/>Submit
                        </button>
                      )}
                      {l.status === 'approved' && (
                        <button className="btn btn-blue btn-sm" onClick={() => printLetter(l)} title="Print">
                          <PrinterIcon/>Print
                        </button>
                      )}
                      {l.status === 'approved' && (
                        <button className="btn btn-purple btn-sm" onClick={() => downloadLetter(l)} title="Download PDF">
                          <ArrowDownTrayIcon/>PDF
                        </button>
                      )}
                      {l.status !== 'approved' && (
                        <button className="btn btn-red btn-sm" onClick={() => deleteLetter(l._id)} title="Delete">
                          <TrashIcon/>Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              </>
            )
          }
        </div>
      </div>
    );
  };
}
